/**
 * Find all occurrences of a Chinese phrase within a text.
 *
 * @param text - The full text to search in
 * @param phrase - The phrase to find
 * @returns An array of index arrays for each occurrence, or empty array if not found
 *
 * @example
 * const text = '因為我很肚餓，因為我沒吃飯。';
 * findAllChinesePhraseIndices(text, '因為'); // [[0, 1], [7, 8]]
 */
export function findAllChinesePhraseIndices(text: string, phrase: string): number[][] {
    const results: number[][] = [];
    let startIndex = 0;

    while (startIndex < text.length) {
        const foundIndex = text.indexOf(phrase, startIndex);

        if (foundIndex === -1) {
            break;
        }

        // Generate array of indices for each character in the phrase
        const indices = Array.from({ length: phrase.length }, (_, i) => foundIndex + i);
        results.push(indices);

        // Move past this occurrence
        startIndex = foundIndex + 1;
    }

    return results;
}

/**
 * Find all occurrences of multiple phrases within a text.
 *
 * @param text - The full text to search in
 * @param phrases - Array of phrases to find
 * @returns A map of phrase to all their occurrences (array of index arrays)
 *
 * @example
 * const text = '因為我很肚餓，因為我沒吃飯。';
 * findAllMultipleChinesePhrases(text, ['因為', '肚餓']);
 * // Map { '因為' => [[0, 1], [7, 8]], '肚餓' => [[4, 5]] }
 */
export function findAllMultipleChinesePhrases(text: string, phrases: string[]): Map<string, number[][]> {
    const result = new Map<string, number[][]>();

    for (const phrase of phrases) {
        result.set(phrase, findAllChinesePhraseIndices(text, phrase));
    }

    return result;
}
